import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import { LegendItem, LegendItemProps } from "./useLegendItems"
import {
    AreaUnit,
    ColorSwatchCategory,
    DistanceUnit,
    GeometryType,
    hexColorSwatches,
    LineStroke,
    LineWidth,
} from "@nova/constants"
import { defaultLineColor } from "plugins/Toolbox/Line/constants"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { selectVisibleLines } from "features/entity-caching/lines/selectors"
import { calculateArea, totalDistance } from "@nova/calculate-geospatial"
import { formatArea, formatDistance } from "@nova/format"
import { config } from "@nova/configuration/browser"
import { selectLegendPosition, selectPDFLegendSize } from "plugins/PDFv2/PDFSlice"
import { isLegendItemInPDFSnapshot } from "./module"
import { useViewport } from "features/map/GlobalViewState"

const defaultPolygonFill = hexColorSwatches[ColorSwatchCategory.PolygonFill][0]
const defaultPolygonOutline = hexColorSwatches[ColorSwatchCategory.PolygonOutline][0]

const getLineSVG = (stroke: LineStroke, color: string, thick: boolean) => {
    return {
        [LineStroke.dotted]: (
            <path
                d="M1.5625 1.5H11.5625"
                stroke={color}
                strokeWidth={thick ? 2 : 1.5}
                strokeLinecap="round"
                strokeDasharray="0.1 3"
            />
        ),
        [LineStroke.squared]: (
            <path
                d="M1.5625 1.5H11.5625"
                stroke={color}
                strokeLinecap="square"
                strokeDasharray="1 3"
                strokeWidth={thick ? 2 : 1}
            />
        ),
        [LineStroke.normal]: (
            <path d="M1.5625 1.5H11.5625" stroke={color} strokeWidth={thick ? 2 : 1} />
        ),
        [LineStroke.dashed]: (
            <path
                d="M1.5625 1.5H11.5625"
                stroke={color}
                strokeLinecap="round"
                strokeWidth={thick ? 2 : 1}
                strokeDasharray="3 3"
            />
        ),
    }[stroke]
}

export const useGeometryLegendItems = ({ offsetX, offsetY, width, height }: LegendItemProps) => {
    const lines = useAppSelector(selectVisibleLines)
    const view = useViewport()

    const { organizationSettingArea } = config.use("organizationSettingArea") as {
        organizationSettingArea: AreaUnit
    }
    const { organizationSettingDistance } = config.use("organizationSettingDistance") as {
        organizationSettingDistance: DistanceUnit
    }

    const legendSize = useAppSelector(selectPDFLegendSize())
    const legendPosition = useAppSelector(selectLegendPosition())

    const lineLegendItems = useMemo(() => {
        const items: LegendItem[] = []
        ;[...lines]
            .sort(
                (a, b) =>
                    (a.geometryType === GeometryType.Line ? 1 : 0) -
                    (b.geometryType === GeometryType.Line ? 1 : 0)
            )
            .forEach((geometry) => {
                if (
                    !geometry.coordinates?.some((point) => {
                        try {
                            const [x, y] = view.project([point.longitude, point.latitude])
                            const isInsideSnapshot = isLegendItemInPDFSnapshot(
                                x,
                                y,
                                {
                                    offsetX,
                                    offsetY,
                                    width,
                                    height,
                                },
                                legendSize,
                                legendPosition
                            )
                            return isInsideSnapshot
                        } catch (err) {
                            console.error(err)
                        }
                    })
                )
                    return

                if (geometry.geometryType === GeometryType.Line) {
                    const line = geometry
                    const distance = totalDistance(geometry.coordinates)
                    items.push({
                        icon: (
                            <svg
                                width="13"
                                height="3"
                                viewBox="0 0 13 3"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                {getLineSVG(
                                    line.stroke || LineStroke.normal,
                                    line.color ? decodeNumberToColor(line.color) : defaultLineColor,
                                    line.width === LineWidth.thick
                                )}
                            </svg>
                        ),
                        title: line.name || "Line",
                        id: line.id,
                        stat: {
                            label: "Distance",
                            stat: formatDistance({
                                value: distance,
                                unit: organizationSettingDistance,
                            }),
                        },
                    })
                } else {
                    const polygon = geometry
                    const area = calculateArea(geometry.coordinates)
                    const distance = totalDistance(geometry.coordinates, true)

                    items.push({
                        icon: (
                            <svg
                                width="17"
                                height="16"
                                viewBox="0 0 17 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    x="2.8125"
                                    y="4.25"
                                    width="11.5"
                                    height="7.5"
                                    fill={
                                        polygon?.fill
                                            ? decodeNumberToColor(polygon?.fill)
                                            : defaultPolygonFill
                                    }
                                    stroke={
                                        polygon?.outline
                                            ? decodeNumberToColor(polygon?.outline)
                                            : defaultPolygonOutline
                                    }
                                    stroke-width={polygon?.width || 1}
                                />
                            </svg>
                        ),
                        title: polygon.name || "Line",
                        id: polygon.id,
                        stat: {
                            label: "Perimeter",
                            stat: formatDistance({
                                value: distance,
                                unit: organizationSettingDistance,
                            }),
                        },
                        secondaryStat: {
                            label: "Area",
                            stat: formatArea({ value: area, unit: organizationSettingArea }),
                        },
                    })
                }
            })
        return items
    }, [
        lines,
        offsetX,
        offsetY,
        width,
        height,
        view,
        organizationSettingArea,
        organizationSettingDistance,
        legendSize,
        legendPosition,
    ])
    return lineLegendItems
}
